/**
 * Google reCAPTCHA v3 (classic) helper.
 *
 * v3 is invisible: there is no checkbox and no challenge. The script scores the
 * visitor in the background and `executeRecaptcha` mints a short-lived token the
 * backend trades with Google for that score.
 *
 * When VITE_RECAPTCHA_SITE_KEY is unset the helper is a no-op and returns null.
 * The backend fails open in the same case, so local dev needs no key.
 */

const SITE_KEY = import.meta.env.VITE_RECAPTCHA_SITE_KEY

let scriptPromise: Promise<void> | null = null

function loadScript(siteKey: string): Promise<void> {
  // Cached so repeated submits reuse the one <script> tag.
  if (scriptPromise) return scriptPromise

  scriptPromise = new Promise<void>((resolve, reject) => {
    const script = document.createElement('script')
    script.src = `https://www.google.com/recaptcha/api.js?render=${encodeURIComponent(siteKey)}`
    script.async = true
    script.defer = true
    script.onload = () => resolve()
    script.onerror = () => {
      // Let the next attempt retry rather than caching the failure forever.
      scriptPromise = null
      reject(new Error('Failed to load reCAPTCHA'))
    }
    document.head.appendChild(script)
  })

  return scriptPromise
}

/** Preload the script so the first submit is not waiting on a network round trip. */
export function preloadRecaptcha(): void {
  if (!SITE_KEY) return
  loadScript(SITE_KEY).catch(() => {
    // Non-fatal: executeRecaptcha reports the failure at submit time.
  })
}

/**
 * Mint a token for `action`. Returns null when reCAPTCHA is not configured or
 * the script could not load, so callers submit without a token and let the
 * backend decide.
 */
export async function executeRecaptcha(action: string): Promise<string | null> {
  if (!SITE_KEY) return null

  try {
    await loadScript(SITE_KEY)
    const grecaptcha = window.grecaptcha
    if (!grecaptcha) return null

    return await new Promise<string>((resolve, reject) => {
      grecaptcha.ready(() => {
        grecaptcha.execute(SITE_KEY, { action }).then(resolve).catch(reject)
      })
    })
  } catch (error) {
    console.error('reCAPTCHA execution failed:', error)
    return null
  }
}
